import React, { useEffect, useState } from "react";
import { Button, Col, Form, FormGroup, Input, Label, Row } from "reactstrap";
import Select from "react-select";
import { GetLanguageString } from "../../helper/Components";
import {
  LoadingSpinner,
  convertToDateString,
} from "../../../constants/const";
import { IoArrowBackSharp } from "react-icons/io5";
import { withRouter } from "react-router-dom";
import { GetCustomStyles, GetSelectedLanguage, compressImageFile } from "../../helper/Method";
import { ApiFinance as ApiFinanceLocal } from "../ApiFinance";
import swal from "sweetalert";
import DatePicker from "react-datepicker";
import { encryptStorage } from "../../../constants/EncryptStorage";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";

import { getMinMaxDates } from "../fiscalYearHelper";
import ShareTransferContextPanel, {
  getShareMaxTransferable,
} from "../ShareTransferContextPanel";

const MarkazShare = ({ history }) => {
  const { t } = useTranslation();
  const loggedInUnitId = parseInt(encryptStorage.getItem("loggedInUnitId"));

  const [loading, setLoading] = useState({
    submit: false,
    calculate: false,
  });

  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);

  const getDefaultShareMonth = () => {
    const today = new Date();
    return today.getDate() <= 20
      ? new Date(today.getFullYear(), today.getMonth() - 1, 1)
      : new Date(today.getFullYear(), today.getMonth(), 1);
  };

  const getShareMonthBounds = (shareMonthDate) => {
    if (!shareMonthDate) return { minDate: null, maxDate: new Date() };
    const year = shareMonthDate.getFullYear();
    const month = shareMonthDate.getMonth();
    const minDate = new Date(year, month, 1);
    const today = new Date();
    return { minDate, maxDate: today };
  };

  const defaultShareMonth = getDefaultShareMonth();
  const defaultProcessedDate = new Date();

  const [formData, setFormData] = useState({
    share_month: defaultShareMonth,
    processed_date: defaultProcessedDate,
    amount: "",
    memo: "",
  });

  const [errors, setErrors] = useState({});
  const [attachedFile, setAttachedFile] = useState(null);

  const { minDate: fiscalMinDate } = getMinMaxDates();
  const getLatestShareMonthMaxDate = () => {
    const today = new Date();
    return today.getDate() <= 20
      ? new Date(today.getFullYear(), today.getMonth(), 0)
      : today;
  };
  const latestShareMonthMaxDate = getLatestShareMonthMaxDate();
  const { minDate, maxDate } = getShareMonthBounds(formData.share_month);

  const sourceAccountType =
    selectedBankAccount?.label1 === "cash_in_hand_account" ||
    selectedBankAccount?.label1 === "cash"
      ? "cash"
      : "amanat";
  const isCashSource = sourceAccountType === "cash";

  const [shareContext, setShareContext] = useState(null);
  const calculatedAmount = Number(shareContext?.minimumShare) || 0;
  const parsedFormAmount = parseFloat(formData.amount);
  const isDeficit =
    calculatedAmount > 0 &&
    !isNaN(parsedFormAmount) &&
    parsedFormAmount > 0 &&
    parsedFormAmount < calculatedAmount;

  useEffect(() => {
    if (!loggedInUnitId) return;
    ApiFinanceLocal()
      .GetFinanceBankAccounts(loggedInUnitId)
      .then((res) => {
        const filtered = (res.data || []).filter(
          (account) =>
            account?.label1 === "cash_in_hand_account" ||
            account?.label1 === "amanat_account"
        );
        setBankAccounts(filtered);
        if (filtered.length > 0) {
          const defaultAcc =
            filtered.find((a) => a.label1 === "amanat_account") || filtered[0];
          setSelectedBankAccount(defaultAcc);
        }
      })
      .catch((err) => {
        console.error("Failed to load bank accounts", err);
      });
  }, [loggedInUnitId]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
    if (field === "amount" && !isCashSource) {
      const nextAmount = parseFloat(value);
      if (!(calculatedAmount > 0 && nextAmount > 0 && nextAmount < calculatedAmount)) {
        setAttachedFile(null);
        setErrors((prev) => ({ ...prev, attachedFile: "" }));
      }
    }
  };

  const handleShareMonthChange = (date) => {
    const selectedMonth = date ?? getDefaultShareMonth();
    const bounds = getShareMonthBounds(selectedMonth);
    let updatedProcessedDate = formData.processed_date;
    if (updatedProcessedDate < bounds.minDate || updatedProcessedDate > bounds.maxDate) {
      const today = new Date();
      if (today >= bounds.minDate && today <= bounds.maxDate) {
        updatedProcessedDate = today;
      } else {
        updatedProcessedDate = bounds.maxDate;
      }
    }
    setFormData((prev) => ({
      ...prev,
      share_month: selectedMonth,
      processed_date: updatedProcessedDate,
    }));
    setErrors((prev) => ({ ...prev, share_month: "", processed_date: "" }));
  };

  useEffect(() => {
    if (!formData.share_month) return;

    setLoading((prev) => ({ ...prev, calculate: true }));
    const formattedDate = convertToDateString(formData.share_month);

    ApiFinanceLocal()
      .GetMarkazShareContext(loggedInUnitId, formattedDate)
      .then((res) => {
        const context = res.data || {};
        setShareContext(context);
        setFormData((prev) => ({
          ...prev,
          amount: (context.minimumShare ?? 0).toString(),
        }));
        setLoading((prev) => ({ ...prev, calculate: false }));
      })
      .catch((err) => {
        console.error(err);
        toast.error(t("funds_markaz_share_calculation_failed"));
        setLoading((prev) => ({ ...prev, calculate: false }));
      });
  }, [formData.share_month, loggedInUnitId, t]);

  const handleFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const MAX_SIZE = 1500 * 1024; // 1.5 MB
    let processedFile = file;

    if (file.type && file.type.startsWith("image/") && file.size > MAX_SIZE) {
      try {
        processedFile = await compressImageFile(file, 1400 * 1024);
      } catch (err) {
        console.error("Image compression error:", err);
      }
    }

    if (processedFile.size > MAX_SIZE) {
      setAttachedFile(null);
      setErrors((prev) => ({
        ...prev,
        attachedFile: isCashSource
          ? (t("file_size_limit_1_5mb") || "File size must be 1.5MB or less.")
          : (t("markaz_share_justification_size_limit") || "File size must not exceed 2MB."),
      }));
      e.target.value = "";
    } else {
      setAttachedFile(processedFile);
      setErrors((prev) => ({ ...prev, attachedFile: "" }));
    }
  };

  const validate = () => {
    const temp = {};
    let isValid = true;

    if (!formData.share_month) {
      temp.share_month = t("common_field_required_error");
      isValid = false;
    }

    if (!formData.processed_date) {
      temp.processed_date = t("common_field_required_error");
      isValid = false;
    }

    if (!selectedBankAccount) {
      temp.bank_account = t("common_field_required_error");
      isValid = false;
    }

    if (!formData.amount || formData.amount.toString().trim() === "") {
      temp.amount = t("common_field_required_error");
      isValid = false;
    } else {
      const parsedAmount = parseFloat(formData.amount);
      const maxTransferable = getShareMaxTransferable(shareContext, sourceAccountType);
      const totalInfaq = Number(shareContext?.totalInfaq) || 0;
      if (isNaN(parsedAmount) || parsedAmount <= 0) {
        temp.amount = t("amount_must_be_greater_than_zero");
        isValid = false;
      } else if (shareContext?.hasPendingRequest) {
        temp.amount = t("share_request_already_pending");
        isValid = false;
      } else if (parsedAmount > totalInfaq) {
        temp.amount = t("share_amount_exceeds_total_infaq");
        isValid = false;
      } else if (parsedAmount > maxTransferable) {
        temp.amount = t("share_amount_exceeds_account_balance");
        isValid = false;
      }
    }

    if (isCashSource) {
      if (!attachedFile) {
        temp.attachedFile = t("markaz_share_deposit_slip_required") || "Deposit slip / payment screenshot is required for Cash in Hand Markaz Share.";
        isValid = false;
      }
    } else if (isDeficit) {
      if (!attachedFile) {
        temp.attachedFile = t("markaz_share_justification_required");
        isValid = false;
      }
    }

    if (!formData.memo || formData.memo.toString().trim() === "") {
      temp.memo = t("common_field_required_error");
      isValid = false;
    }

    setErrors(temp);
    return isValid;
  };

  const formatLocalDateString = (d) => {
    if (!d) return "";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!validate()) {
      return;
    }

    const enteredAmount = parseFloat(formData.amount);

    let finalMemo = formData.memo ? formData.memo.trim() : "";
    if (formData.share_month) {
      const monthName = formData.share_month.toLocaleString("en-US", { month: "long" });
      const year = formData.share_month.getFullYear();
      if (!finalMemo.toLowerCase().includes(monthName.toLowerCase())) {
        finalMemo = finalMemo ? `${finalMemo} (${monthName} ${year})` : `${monthName} ${year}`;
      }
    }

    const executeSubmit = () => {
      setLoading((prev) => ({ ...prev, submit: true }));

      const requiresForm = isCashSource || isDeficit;
      const request = requiresForm
        ? (() => {
            const payload = new FormData();
            payload.append("unitId", loggedInUnitId);
            payload.append("date", formatLocalDateString(formData.processed_date));
            payload.append("share_month", formatLocalDateString(formData.share_month));
            payload.append("amount", enteredAmount);
            payload.append("memo", finalMemo);
            payload.append("isRequest", "true");
            payload.append("source_account_type", sourceAccountType);
            payload.append("account_type_code", selectedBankAccount?.label1 || "");
            if (attachedFile) {
              payload.append("File", attachedFile);
            }
            return ApiFinanceLocal().MarkazShareTransferWithJustification(payload);
          })()
        : ApiFinanceLocal().MarkazShareTransfer({
            date: formatLocalDateString(formData.processed_date),
            share_month: formatLocalDateString(formData.share_month),
            amount: enteredAmount,
            memo: finalMemo,
            unitId: loggedInUnitId,
            isRequest: true,
            source_account_type: "amanat",
          });

      request
        .then((res) => {
          const data = res.data;
          if (data?.statusCode === 200) {
            let msg = isCashSource
              ? (t("finance_markaz_share_requested") || "Markaz Share transfer request sent successfully.")
              : (t("finance_markaz_share_amanat_requested") || "Your Markaz Share from Amanat Account request has been sent to Markaz.");
            if (t(data.message) !== data.message) {
              msg = t(data.message);
            }
            swal({
              title: t("common_success") || "Success",
              text: msg,
              icon: "success",
              buttons: "OK",
            }).then(() => {
              history.goBack();
            });
            return;
          }

          swal({
            title: t("common_error") || "Error",
            text: data?.message || "Failed to add Markaz Share",
            icon: "error",
            buttons: "OK",
          });
          setLoading((prev) => ({ ...prev, submit: false }));
        })
        .catch((err) => {
          console.error(err);
          const raw = err?.response?.data;
          const message = typeof raw === "string" ? raw : raw?.message;
          swal({
            title: t("common_error") || "Error",
            text: message && t(message) !== message ? t(message) : "Unable to connect to server. Please try again.",
            icon: "error",
            buttons: "OK",
          });
          setLoading((prev) => ({ ...prev, submit: false }));
        });
    };

    if (isDeficit) {
      swal({
        title: t("share_request_confirm_title"),
        text: t("funds_markaz_share_request_confirm_text"),
        icon: "warning",
        buttons: {
          cancel: t("share_request_confirm_btn_cancel"),
          confirm: t("share_request_confirm_btn_proceed"),
        },
      }).then((confirmed) => {
        if (confirmed) {
          executeSubmit();
        }
      });
      return;
    }

    executeSubmit();
  };

  return (
    <div className="wrapper-custom wrapper wrapper-content animated fadeInRight">
      <ToastContainer
        autoClose={3000}
        bodyClassName={GetSelectedLanguage()}
        closeOnClick
        pauseOnHover={false}
        draggable
      />
      <div className="ibox">
        <div className="ibox-title d-flex justify-content-between align-items-center">
          <h5>{<GetLanguageString props="funds_markaz_share" />}</h5>
          <Button
            className={GetCustomStyles().btn_style}
            color="white"
            size="xs"
            onClick={() => history.goBack()}
          >
            <IoArrowBackSharp
              color="#18A689"
              size={15}
              style={{ marginRight: 5, marginBottom: 1 }}
            />
            {<GetLanguageString props="courses_participants_back" />}
          </Button>
        </div>

        <div className="ibox-content">
          {loading.submit ? (
            LoadingSpinner()
          ) : (
            <Form onSubmit={handleSubmit} noValidate>
              <ShareTransferContextPanel
                context={shareContext}
                loading={loading.calculate}
                minimumLabelKey="share_minimum_markaz"
                sourceAccountType={sourceAccountType}
              />
              <Row>
                <Col>
                  <FormGroup>
                    <Label className="col-form-label">
                      {<GetLanguageString props="funds_share_month" />}
                    </Label>
                    <DatePicker
                      showYearDropdown
                      dropdownMode="select"
                      placeholderText="Select month"
                      className="form-control no-drag"
                      selected={formData.share_month}
                      onChange={handleShareMonthChange}
                      showMonthYearPicker
                      minDate={fiscalMinDate}
                      maxDate={latestShareMonthMaxDate}
                      dateFormat="MMMM yyyy"
                    />
                    {errors.share_month && <h4 className="text-error">{errors.share_month}</h4>}
                  </FormGroup>
                </Col>

                <Col>
                  <FormGroup>
                    <Label className="col-form-label">
                      {<GetLanguageString props="funds_processed_date" />}
                    </Label>
                    <DatePicker
                      showYearDropdown
                      dropdownMode="select"
                      placeholderText="Select date"
                      className="form-control no-drag"
                      selected={formData.processed_date}
                      onChange={(date) => handleChange("processed_date", date ?? new Date())}
                      minDate={minDate}
                      maxDate={maxDate}
                      dateFormat="dd/MM/yyyy"
                    />
                    {errors.processed_date && <h4 className="text-error">{errors.processed_date}</h4>}
                  </FormGroup>
                </Col>

                <Col>
                  <FormGroup>
                    <Label className="col-form-label">
                      {<GetLanguageString props="account_type" />}
                    </Label>
                    <Select
                      options={bankAccounts}
                      value={selectedBankAccount}
                      className="basic-single no-drag"
                      classNamePrefix="select"
                      onChange={(option) => {
                        setSelectedBankAccount(option);
                        setErrors((prev) => ({ ...prev, bank_account: "" }));
                      }}
                    />
                    {errors.bank_account && <h4 className="text-error">{errors.bank_account}</h4>}
                  </FormGroup>
                </Col>

                <Col>
                  <FormGroup>
                    <Label className="col-form-label">
                      {<GetLanguageString props="funds_add_fund_amount" />}
                    </Label>
                    <Input
                      name="amount"
                      type="text"
                      value={formData.amount}
                      onChange={(e) => handleChange("amount", e.target.value)}
                      className="no-drag"
                      invalid={!!errors.amount}
                      disabled={loading.calculate}
                    />
                    {loading.calculate ? (
                      <small className="text-muted d-block mt-1">Calculating share...</small>
                    ) : (
                      isDeficit && (
                        <div
                          style={{
                            backgroundColor: "#FEF3C7",
                            border: "1px solid #F59E0B",
                            borderRadius: "6px",
                            padding: "6px 10px",
                            marginTop: "6px",
                            color: "#92400E",
                            fontSize: "12px",
                            fontWeight: "600",
                          }}
                        >
                          <i className="fa fa-exclamation-triangle mr-1" style={{ color: "#D97706", marginRight: "4px" }}></i>
                          {t("markaz_share_deficit_warning", { amount: calculatedAmount.toLocaleString() })}
                        </div>
                      )
                    )}
                    {errors.amount && <h4 className="text-error no-drag">{errors.amount}</h4>}
                  </FormGroup>
                </Col>

                <Col>
                  <FormGroup>
                    <Label className="col-form-label">
                      {<GetLanguageString props="transactions_memo" />}
                    </Label>
                    <Input
                      name="memo"
                      type="text"
                      value={formData.memo}
                      onChange={(e) => handleChange("memo", e.target.value)}
                      className="no-drag"
                      invalid={!!errors.memo}
                    />
                    {errors.memo && <h4 className="text-error no-drag">{errors.memo}</h4>}
                  </FormGroup>
                </Col>
              </Row>

              {(isCashSource || isDeficit) && (
                <Row className="mt-2">
                  <Col md="6">
                    <div
                      className="no-drag"
                      style={{
                        backgroundColor: isCashSource ? "#F0FDF4" : "#FEF3C7",
                        border: isCashSource ? "1px solid #86EFAC" : "1px solid #F59E0B",
                        borderRadius: "8px",
                        padding: "12px 14px",
                      }}
                    >
                      <FormGroup className="mb-0">
                        <Label
                          className="col-form-label"
                          style={{
                            color: isCashSource ? "#166534" : "#92400E",
                            fontWeight: 700,
                          }}
                        >
                          <i
                            className="fa fa-paperclip"
                            style={{
                              marginRight: "6px",
                              color: isCashSource ? "#16A34A" : "#D97706",
                            }}
                          ></i>
                          {isCashSource
                            ? (t("markaz_share_deposit_slip_attachment") || "Deposit Slip / Payment Screenshot (Required, Max 1.5MB)")
                            : <GetLanguageString props="markaz_share_justification_attachment" />}
                        </Label>
                        <Input
                          type="file"
                          accept="image/*,application/pdf"
                          multiple={false}
                          name="file"
                          className="form-control"
                          onChange={handleFileChange}
                        />
                        {errors.attachedFile && (
                          <h4 className="text-error mt-1">{errors.attachedFile}</h4>
                        )}
                        {attachedFile && (
                          <div
                            className="d-flex align-items-center justify-content-between p-2 mt-2 rounded"
                            style={{
                              backgroundColor: "#FFFFFF",
                              border: "1px solid #CBD5E1",
                              fontSize: "12px",
                              color: "#334155",
                            }}
                          >
                            <div
                              className="d-flex align-items-center gap-2 text-truncate"
                              style={{ maxWidth: "80%" }}
                            >
                              <i className="fa fa-file text-success"></i>
                              <span className="text-truncate fw-bold">{attachedFile.name}</span>
                              <span className="badge bg-light text-muted border px-1">
                                {(attachedFile.size / 1024).toFixed(1)} KB
                              </span>
                            </div>
                            <Button
                              color="link"
                              size="sm"
                              className="text-danger p-0 ms-2 text-decoration-none"
                              onClick={() => setAttachedFile(null)}
                              title={t("remove_file") || "Remove file"}
                              type="button"
                            >
                              <i className="fa fa-times"></i>
                            </Button>
                          </div>
                        )}
                      </FormGroup>
                    </div>
                  </Col>
                </Row>
              )}

              <Row className="mt-3">
                <Col md="9"></Col>
                <Col md="3" className="text-end">
                  <Button
                    type="submit"
                    color="primary"
                    disabled={loading.submit || loading.calculate}
                  >
                    {<GetLanguageString props="funds_submit" />}
                  </Button>
                </Col>
              </Row>
            </Form>
          )}
        </div>
      </div>
    </div>
  );
};

export default withRouter(MarkazShare);
